import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const CustomerContext = createContext();

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('customer-token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/customer/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setCustomer(response.data);
        } catch (error) {
          localStorage.removeItem('customer-token');
          setToken(null);
          setCustomer(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const register = async (name, email, password, phone = '') => {
    const response = await axios.post(`${API}/customer/register`, { 
      name, email, password, phone 
    });
    const newToken = response.data.token;
    localStorage.setItem('customer-token', newToken);
    setToken(newToken);
    setCustomer(response.data.customer);
    return response.data;
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/customer/login`, { email, password });
    const newToken = response.data.token;
    localStorage.setItem('customer-token', newToken);
    setToken(newToken);
    setCustomer(response.data.customer);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('customer-token');
    setToken(null);
    setCustomer(null);
  };

  const updateProfile = async (data) => {
    const response = await axios.put(`${API}/customer/me`, data, {
      headers: { Authorization: `Bearer ${token}` }
    });
    setCustomer(response.data);
    return response.data;
  };

  const getOrders = async () => {
    const response = await axios.get(`${API}/customer/orders`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    return response.data;
  };

  const getAuthHeader = () => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  return (
    <CustomerContext.Provider value={{
      customer,
      token,
      loading,
      register,
      login,
      logout,
      updateProfile,
      getOrders,
      getAuthHeader,
      isAuthenticated: !!customer
    }}>
      {children}
    </CustomerContext.Provider>
  );
};
